import PropTypes from 'prop-types';
import {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import SettingsIcon from '@mui/icons-material/Settings';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import BlankContent from '../common/BlankContent';
import CustomCheckbox from '../common/CustomCheckbox';
import SessionTableRow from './SessionTableRow';
import SessionListItem from './SessionListItem';
import './SessionsTable.scss';
import CustomPagination from "../common/CustomPagination";
import BetaFeature from '../common/BetaFeature';
import {setColumns} from '../../redux/reducers/sessionTable';

const getColumns = ({
  showAllProviders, 
  isPatient, 
  setSettingsDialogOpen
}) => [
  {
    label: 'Status',
    key: 'status',
    align: 'left',
    required: true,
  },
  {
    label: 'Provider',
    key: 'provider',
    align: 'left',
    hidden: !showAllProviders,
  },
  {
    label: 'Date/Time',
    key: 'date',
    align: 'left',
    required: true,
  },
  {
    label: isPatient ? 'Provider' : 'Patient',
    key: 'patient',
    align: 'left',
    required: true,
  },
  {
    label: 'Case',
    key: 'case',
    align: 'left',
    hidden: isPatient,
  },
  {
    label: 'Type',
    key: 'category',
    align: 'left',
    hidden: isPatient,
  },
  {
    label: 'Contact #',
    key: 'patient.phone',
    align: 'left',
    hidden: isPatient,
  },
  {
    label: 'Paid',
    key: 'total',
    align: 'center',
  },
  {
    label: 'Session Type',
    key: 'session_type', 
    align: 'left',
    hidden: isPatient,
  },
  {
    label: 'Location',
    key: 'location',
    align: 'left',
  },
  {
    label: 'Note',
    key: 'comment',
    align: 'left',
    hidden: isPatient,
  },
  {
    label: (
      isPatient ? '' :
      <Button 
        size='small'
        onClick={() => setSettingsDialogOpen(true)}
      >
        <SettingsIcon/>{' '}
        Settings
      </Button>
    ),
    key: 'actions',
    align: 'right',
  },
];

const ColumnsDialog = props => {
  const dispatch = useDispatch();
  const selectedColumns = useSelector(state => state.sessionTable.columns);
  const onSetColumns = columns => dispatch(setColumns(columns));
  return (
    <Dialog open={props.isOpen} onClose={props.onClose}>
      <DialogContent>
        <Typography variant='h3' sx={{marginBottom: '1rem'}} align='center'>
          Select Columns
        </Typography>
        <Grid container>
          {(props?.allColumns || []).filter(c => c.key !== 'actions' && !c.hidden).map(c => 
            <Grid item xs={12} md={6} key={c.key}>
              <CustomCheckbox
                label={c.label}
                defaultValue={(selectedColumns || []).find(_c => _c === c.key)}
                disabled={!!c?.required}
                onChange={e => (
                  e.target.checked
                  ? onSetColumns([...(selectedColumns || []), c.key])
                  : onSetColumns((selectedColumns || []).filter(_c => _c !== c.key))
                )}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.onClose()}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const SessionTable = props => {
  const {showAllProviders, isPatient} = props;
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const allColumns = getColumns({setSettingsDialogOpen, showAllProviders, isPatient});
  const selectedColumns = useSelector(state => state.sessionTable.columns) || [];
  const isEmpty = !props.sessions || !props.sessions.length;
  const columns = allColumns
    .filter(c => !c.hidden)
    .filter(c => 
      c.required 
      || (selectedColumns.length > 0 ? selectedColumns.includes(c.key) : true)
    );
  return (
    <>
      <Paper sx={{ width: '100%', display: {xs: 'none', sm: 'block'}}} elevation={0}>
        <ColumnsDialog
          allColumns={allColumns}
          isOpen={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
        />
        <TableContainer>
          <Table className='session-table'>
            <TableHead>
              <TableRow sx={{'th': {paddingBottom: '6px'}}}>
                {
                  columns.map(c =>
                    c.isBetaFeature ? 
                      <BetaFeature key={c.key} hideTag={true}>
                        <TableCell key={c.key} align={c.align || 'left'}>
                          {c.label || c.content}
                        </TableCell>
                      </BetaFeature>
                    : <TableCell key={c.key} align={c.align || 'left'}>
                      {c.label}
                    </TableCell>
                  )
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {(props.rows|| []).map(row =>
                <SessionTableRow
                  {...row}
                  columns={columns}
                  showAllProviders={props.showAllProviders}
                  isPatient={props.isPatient}
                  afterSave={() => {
                    console.log('SessionTableRow.afterSave');
                    props.afterSave();
                  }}
                />
              )}
            </TableBody>
          </Table>
          {
            isEmpty
            ? <BlankContent
              title='No sessions found'
              Icon={QuestionMarkIcon}
            />
            : null
          }
        </TableContainer>
        {
          !isEmpty &&
          <Box sx={{marginTop: '0.625rem'}}>
            <CustomPagination
              pageSize={props.pageSize}
              pageIndex={props.pageIndex}
              count={props.count}
              onPageChange={(e,num) => props.onPageChange(e, num)}
            ></CustomPagination>
          </Box>
        }

      </Paper>
      {/* If screen size is xs then replace the table with a mobile-friendly list */}
      <Box sx={{display: {'xs': 'block', 'sm': 'none', 'md': 'none', 'lg': 'none'}}}>
        <Stack spacing={1} padding={1}>
          {(props.rows|| []).map(row =>
            <SessionListItem
              session={row.data}
              buttons={row.buttons}
              isPatient={props.isPatient}
              onSave={props.afterSave}
              isMobile={props.isMobile}
              showAllProviders={props.showAllProviders}
            />
          )}
        </Stack>
        <Box style={{marginTop: '1em'}}>
          { props.rows && props.rows.length
            ? <Stack direction='row' justifyContent='center'>
                <CustomPagination
                    pageSize={props.pageSize}
                    pageIndex={props.pageIndex}
                    count={props.count}
                    onPageChange={props.onPageChange}
                ></CustomPagination>
            </Stack>
            : null
          }
        </Box>
        {
          isEmpty
          ? <BlankContent
            title='No sessions found'
            Icon={QuestionMarkIcon}
          />
          : null
        }
      </Box>
    </>
  );
};

SessionTable.defaultProps = {
  showAllProviders: false,
};

SessionTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.object, // data for the row
    buttons: PropTypes.arrayOf(PropTypes.element),
  })),
  showAllProviders: PropTypes.bool,
  count: PropTypes.number,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  afterSave: PropTypes.func,
};

export default SessionTable;
